import { useRecruitmentApi } from 'infrastructure/api';
import { ENGLISH_LEVEL_OPTIONS } from 'domain/shared/candidate';
import { cloneDeep } from 'lodash';
import { useCrudService } from 'framework/crud';
import { LoadingParams } from 'framework/list';
import { useShowContext } from 'framework/show';
import { COMPETENCY_LEVEL } from 'domain/shared/newVacancy';

const SOURCING_API_PATH = '/candidates/specialization';

export const SOURCING_FILTERS = {
  stricts: 'stricts',
  level: 'qualificationLevel',
  location: 'location',
  englishLevel: 'englishLevel',
  skills: 'skills',
  tags: 'tags',
  lastInteraction: 'lastInteraction',
} as const;

const STRICTS = {
  STRICT: 'STRICT',
  NON_STRICT: 'NON_STRICT',
} as const;

const useVacancySourcingService = () => {
  const { data: show } = useShowContext();
  const vacancyGroupData = show.getData();

  const { search: baseSearch, ...baseCrudRequests } = useCrudService({
    listResourceUrl: SOURCING_API_PATH,
    singleResourceUrl: `${SOURCING_API_PATH}/id`,
    useService: useRecruitmentApi,
  });

  const search = async (request: LoadingParams) => {
    const requestData = cloneDeep(request);
    const allEnglishLevels = Object.values(ENGLISH_LEVEL_OPTIONS);
    const filtres = requestData?.filter;

    const technologyField = vacancyGroupData.technology
      ? {
          value: [vacancyGroupData.technology],
          mode: STRICTS.STRICT,
        }
      : null;
    const response = await baseSearch({
      pageNumber: requestData.pageNumber || 0,
      pageSize: requestData.pageSize || 30,
      sort: requestData.sort,
      fields: {
        specialization: vacancyGroupData.specialization,
        technology: technologyField,
        ...Object.entries(filtres || {})
          .filter(([key]) => key !== SOURCING_FILTERS.stricts)
          .reduce((acc: { [key: string]: any }, [key, value]) => {
            let finalValue = value;
            if (key === SOURCING_FILTERS.lastInteraction) {
              if (Array.isArray(value)) {
                const [from = '', to = ''] = value;
                finalValue = { from, to };
              } else if (value && typeof value === 'object') {
                finalValue = { from: value.from || '', to: value.to || '' };
              } else {
                finalValue = { from: '', to: '' };
              }
            } else if (key === SOURCING_FILTERS.englishLevel) {
              finalValue = allEnglishLevels.slice(allEnglishLevels.indexOf(value[0]));
            }

            acc[key] = {
              value: finalValue,
              mode: requestData?.filter?.stricts?.includes(key) ? STRICTS.STRICT : STRICTS.NON_STRICT,
            };
            return acc;
          }, {}),
      },
    });

    return {
      ...response,
      result: response.result.map((item: any) => {
        const currentSpecialization = item.specializations.find(
          (specialization: any) => specialization.specialization === vacancyGroupData.specialization,
        );

        return {
          ...item,
          specialization: currentSpecialization.specialization,
          level: item.qualificationLevel
            ? COMPETENCY_LEVEL[item.qualificationLevel as keyof typeof COMPETENCY_LEVEL]
            : null,
          isQualificationLevelConfirmed: item.specializations.some((specialization: any) => specialization.isConfirmed),
        };
      }),
    };
  };

  return {
    search,
    ...baseCrudRequests,
  };
};

export default useVacancySourcingService;
